// Categorías de plantillas que pertenecen a un modo con catálogo CURADO, y que por
// eso nunca deben aparecer en el catálogo genérico por rubro
// (ADR-2026-09-10-el-catalogo-de-moda-no-toma-categorias-curadas).
//
// Vive en su propio archivo —y no dentro de `templatesBellezaApi`— para no armar un
// import circular: `templatesBellezaApi` ya importa de `templatesApi`, y `templatesApi`
// necesita esta lista para excluirla. Con la constante acá, los dos la importan de un
// tercero y nadie se apunta de vuelta.

import { RUBRO_BELLEZA } from '../../lib/rubro';

/**
 * Las categorías del modo Plantillas de Belleza y Cuidado Personal, por
 * `template_categorias.slug`, en el orden en que se muestran.
 *
 * Es la CURADURÍA del modo: la lista está en el código y no sale de una consulta por
 * rubro, así que una categoría nueva de Moda no aparece sola en Belleza ni al revés.
 * Cuando sean muchas y las cargue alguien que no toca el repo, el movimiento es
 * pasarla a una columna de `template_categorias`.
 */
export const CATEGORIAS_BELLEZA = [
  'solo-producto-belleza',
  'con-modelos-belleza',
] as const;

/**
 * Todas las categorías curadas, de todos los modos, para preguntar rápido si una
 * categoría "es de otro".
 *
 * Hoy son sólo las de Belleza. El día que haya un tercer modo con catálogo propio, su
 * lista se suma acá y el catálogo genérico la excluye sin tocar nada más.
 */
export const CATEGORIAS_CURADAS: ReadonlySet<string> = new Set<string>(
  CATEGORIAS_BELLEZA,
);

/**
 * El SUFIJO que marca a una categoría como del modo de Belleza —
 * ADR-2026-09-10-una-categoria-es-de-belleza-por-su-slug.
 *
 * Cargar plantillas nuevas para Belleza tenía que ser sólo cargarlas, y con la lista de
 * arriba hardcodeada era además editar el repo: una categoría nueva no aparecía en
 * Belleza (el fetch lee la lista) y sí se colaba en Moda (el filtro excluye la lista).
 * El peor de los dos mundos, y en silencio.
 *
 * Con la convención, el slug es el que decide: `mascarillas-belleza` entra sola al
 * catálogo de Belleza y queda afuera del de Moda, sin tocar código. Las dos de la lista
 * ya lo cumplen (`solo-producto-belleza`, `con-modelos-belleza`), así que la lista
 * sobrevive sólo por el ORDEN — es la curaduría de cuáles van primero.
 */
export const SUFIJO_CATEGORIA_BELLEZA = '-belleza';

/**
 * Si una categoría pertenece al modo Plantillas de Belleza: por la lista curada o por
 * el sufijo del slug. Lo preguntan los DOS catálogos —el de Belleza para incluirla, el
 * de Moda para excluirla—, así que la respuesta tiene que salir de un solo lugar.
 */
export const esCategoriaDeBelleza = (slug: string): boolean =>
  CATEGORIAS_CURADAS.has(slug) || slug.endsWith(SUFIJO_CATEGORIA_BELLEZA);

/**
 * Rubro CENTINELA que llevan hoy las plantillas de `solo-producto-belleza` en la base
 * — ADR-2026-09-11-las-plantillas-de-belleza-quedan-ultimas-por-un-rubro-que-nadie-tiene.
 *
 * Es un valor que ninguna empresa tiene, puesto a mano para que el catálogo genérico
 * de producción —que todavía no tiene el filtro de este archivo— no las agrupara por
 * rubro. Se lee acá para que esas filas cuenten como de Belleza igual: mientras el
 * centinela siga en la base, `rubros` no dice el rubro de verdad, y la respuesta a
 * "¿esta plantilla es de Belleza?" tiene que ser la misma antes y después de
 * revertirlo.
 */
export const RUBRO_CENTINELA_BELLEZA = '__solo-modo-belleza__';

/**
 * `true` si la plantilla DECLARA el rubro Belleza en `templates.rubros` — el centinela
 * de arriba cuenta como declaración.
 *
 * Es el contrato de datos que separa los dos catálogos junto con el sufijo del slug
 * (`esCategoriaDeBelleza`): cargar una plantilla para Belleza es cargarla con
 * `rubros = {'Belleza y Cuidado Personal'}`, y con eso sola se va al catálogo de
 * Belleza y desaparece del de Moda, sin tocar el repo ni depender de en qué categoría
 * la pusieron — ADR-2026-09-11-la-plantilla-va-al-catalogo-de-su-rubro.
 *
 * Ojo con el caso de una plantilla que declare LOS DOS rubros: cuenta como de Belleza
 * y no se ve en Moda. Es a propósito —lo pedido es que los dos catálogos no se crucen—
 * y hoy no hay ninguna fila así; si alguna vez hace falta la misma escena en los dos
 * modos, es una decisión explícita (dos filas, una por rubro) y no un efecto de cómo
 * quedó cargado el array.
 */
export const plantillaDeclaraBelleza = (
  rubros: readonly string[] | null | undefined,
): boolean =>
  (rubros ?? []).some(
    (r) => r === RUBRO_BELLEZA || r === RUBRO_CENTINELA_BELLEZA,
  );

/**
 * La pregunta que usan los DOS catálogos para repartirse una fila de `templates`: el de
 * Belleza para quedársela, el de Moda para dejarla afuera. Una sola función para que la
 * respuesta no pueda diferir entre las dos pantallas y una plantilla quede en las dos
 * —o en ninguna—.
 *
 * Dos señales, cualquiera alcanza:
 *  • el RUBRO declarado en la fila (`plantillaDeclaraBelleza`), que es el contrato para
 *    cargar plantillas nuevas;
 *  • el SLUG de su categoría (`esCategoriaDeBelleza`), que es la curaduría: todo lo que
 *    vive en una categoría `-belleza` es del modo, declare lo que declare.
 */
export const esPlantillaDeBelleza = (
  rubros: readonly string[] | null | undefined,
  slugCategoria: string | null | undefined,
): boolean =>
  plantillaDeclaraBelleza(rubros) ||
  (slugCategoria != null && esCategoriaDeBelleza(slugCategoria));
